import { useState, useEffect } from 'react';
import { Shield, Trash2, Database, Clock, Save, Loader2, AlertTriangle, Lock, Eye, EyeOff, ScrollText, ChevronLeft, ChevronRight, Download, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI, authAPI } from '@/lib/api';
import { formatDateTime } from '@/lib/dateUtils';

export default function AdminSettings() {
  const [retentionMonths, setRetentionMonths] = useState(12);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Admin password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  // Default schedule
  const [defaultCheckin, setDefaultCheckin] = useState('');
  const [defaultCheckout, setDefaultCheckout] = useState('');
  const [gracePeriod, setGracePeriod] = useState(15);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Audit logs
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    adminAPI.getSettings().then(res => {
      setRetentionMonths(res.data.retention_months ?? 12);
      setDefaultCheckin(res.data.default_checkin_time || '');
      setDefaultCheckout(res.data.default_checkout_time || '');
      setGracePeriod(res.data.grace_period_minutes ?? 15);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateRetention(retentionMonths);
      toast.success(`Retention period set to ${retentionMonths} months`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      await adminAPI.updateDefaultSchedule(defaultCheckin, defaultCheckout, gracePeriod);
      toast.success('Default schedule saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const res = await adminAPI.runDataCleanup();
      setCleanupResult(res.data);
      toast.success(`Cleaned up ${res.data.attendance_deleted} old records`);
      setShowConfirm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPw) { toast.error('Enter current password'); return; }
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    setPwSaving(true);
    try {
      await authAPI.adminChangePassword(currentPw, newPw);
      toast.success('Admin password changed successfully!');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  const fetchLogs = async (page = 1) => {
    setLogsLoading(true);
    try {
      const res = await adminAPI.getAuditLogs(page, 20);
      setLogs(res.data.logs || []);
      setLogsTotal(res.data.total || 0);
      setLogsPage(page);
    } catch {
      toast.error('Failed to load audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleShowLogs = () => {
    if (!showLogs) fetchLogs(1);
    setShowLogs(!showLogs);
  };

  const actionColors = {
    LOGIN_SUCCESS: 'bg-[#026536]/10 text-[#026536]',
    ADMIN_LOGIN_SUCCESS: 'bg-[#026536]/10 text-[#026536]',
    LOGIN_FAILED: 'bg-red-50 text-red-500',
    ADMIN_LOGIN_FAILED: 'bg-red-50 text-red-500',
    PASSWORD_CHANGE_FAILED: 'bg-red-50 text-red-500',
    ADMIN_PASSWORD_CHANGE_FAILED: 'bg-red-50 text-red-500',
    EMPLOYEE_DELETED: 'bg-red-50 text-red-500',
    DATA_CLEANUP: 'bg-red-50 text-red-500',
    CHECKIN: 'bg-blue-50 text-blue-600',
    CHECKOUT: 'bg-blue-50 text-blue-600',
    DATA_EXPORT: 'bg-[#dea439]/10 text-[#dea439]',
  };

  const totalPages = Math.ceil(logsTotal / 20);

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in max-w-2xl">
        <div>
          <h1 data-testid="settings-title" className="text-2xl font-extrabold text-[#1a2529] tracking-tight">Settings</h1>
          <p className="text-sm text-[#1a2529]/50 mt-0.5">Account, data privacy and compliance configuration</p>
        </div>

        {/* Admin Password Change */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
            <Lock size={16} className="text-[#026536]" /> Admin Password
          </h3>
          <p className="text-xs text-[#1a2529]/40 mb-4">Change your admin login password.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-[#1a2529]/70">Current Password</Label>
              <div className="relative mt-1">
                <Input data-testid="admin-current-pw" type={showCurrentPw ? 'text' : 'password'} value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)} placeholder="Enter current password" className="h-10 pr-10" />
                <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30">
                  {showCurrentPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1a2529]/70">New Password</Label>
              <div className="relative mt-1">
                <Input data-testid="admin-new-pw" type={showNewPw ? 'text' : 'password'} value={newPw}
                  onChange={e => setNewPw(e.target.value)} placeholder="At least 6 characters" className="h-10 pr-10" />
                <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30">
                  {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1a2529]/70">Confirm New Password</Label>
              <Input data-testid="admin-confirm-pw" type="password" value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)} placeholder="Re-enter new password" className="h-10 mt-1"
                onKeyDown={e => e.key === 'Enter' && handleChangePassword()} />
            </div>
          </div>
          <Button data-testid="save-admin-pw" onClick={handleChangePassword} disabled={pwSaving}
            className="mt-4 h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
            {pwSaving ? <Loader2 className="animate-spin" size={14} /> : <Lock size={14} />}
            Change Password
          </Button>
        </div>

        {/* Data Retention */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
            <Clock size={16} className="text-[#026536]" /> Default Check-in / Check-out Time
          </h3>
          <p className="text-xs text-[#1a2529]/40 mb-4">Used for employees who don't have an individual schedule set.</p>
          <div className="flex items-end gap-3 mb-3">
            <div className="flex-1">
              <Label className="text-xs font-semibold text-[#1a2529]/70">Default Check-in</Label>
              <Input data-testid="default-checkin" type="time" value={defaultCheckin}
                onChange={(e) => setDefaultCheckin(e.target.value)} className="h-10 mt-1" />
            </div>
            <div className="flex-1">
              <Label className="text-xs font-semibold text-[#1a2529]/70">Default Check-out</Label>
              <Input data-testid="default-checkout" type="time" value={defaultCheckout}
                onChange={(e) => setDefaultCheckout(e.target.value)} className="h-10 mt-1" />
            </div>
          </div>
          <div className="flex items-end gap-3 mb-2">
            <div className="flex-1">
              <Label className="text-xs font-semibold text-[#1a2529]/70">Late Grace Period (minutes)</Label>
              <Input data-testid="grace-period-input" type="number" min={0} max={120} value={gracePeriod}
                onChange={(e) => setGracePeriod(parseInt(e.target.value) || 0)} className="h-10 mt-1" />
              <p className="text-[10px] text-[#1a2529]/30 mt-1">Employees checking in more than {gracePeriod} min after scheduled time will be marked late. Set to 0 for no grace.</p>
            </div>
            <Button data-testid="save-schedule-btn" onClick={handleSaveSchedule} disabled={scheduleSaving}
              className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
              {scheduleSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
            </Button>
          </div>
          <p className="text-[10px] text-[#1a2529]/30">Leave check-in/check-out blank to disable late marking for employees without individual schedules.</p>
        </div>

        {/* Data Retention */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
            <Database size={16} className="text-[#026536]" /> Data Retention Policy
          </h3>
          <p className="text-xs text-[#1a2529]/40 mb-4">Attendance records older than this period will be permanently deleted during cleanup.</p>
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="h-6 w-6 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-end gap-3 mb-4">
                <div className="flex-1">
                  <Label className="text-xs font-semibold text-[#1a2529]/70">Retention Period (months)</Label>
                  <Input data-testid="retention-input" type="number" min={1} max={120} value={retentionMonths}
                    onChange={(e) => setRetentionMonths(parseInt(e.target.value) || 12)} className="h-10 mt-1" />
                </div>
                <Button data-testid="save-retention-btn" onClick={handleSave} disabled={saving}
                  className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Save
                </Button>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#1a2529]/40">
                <Database size={12} /> Records older than {retentionMonths} months will be eligible for deletion
              </div>
            </>
          )}
        </div>

        {/* Data Cleanup */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
            <Trash2 size={16} className="text-red-500" /> Data Cleanup
          </h3>
          <p className="text-xs text-[#1a2529]/40 mb-4">Permanently delete attendance and sanction records older than the retention period.</p>
          {!showConfirm ? (
            <Button data-testid="start-cleanup-btn" onClick={() => setShowConfirm(true)} variant="outline"
              className="h-10 border-red-200 text-red-500 hover:bg-red-50 font-semibold gap-2">
              <Trash2 size={14} /> Run Cleanup Now
            </Button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-red-500 mt-0.5 shrink-0" size={16} />
                <div>
                  <p className="text-sm font-semibold text-red-600">Confirm Permanent Deletion</p>
                  <p className="text-xs text-red-400 mt-0.5">All records older than <strong>{retentionMonths} months</strong> will be permanently deleted.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="confirm-cleanup-btn" onClick={handleCleanup} disabled={cleaning} size="sm"
                  className="bg-red-500 hover:bg-red-600 text-white font-medium gap-1.5">
                  {cleaning ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />} Yes, Delete
                </Button>
                <Button data-testid="cancel-cleanup-btn" onClick={() => setShowConfirm(false)} size="sm" variant="outline" className="font-medium">
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {cleanupResult && (
            <div data-testid="cleanup-result" className="mt-4 bg-[#f4f4f5] rounded-xl p-4 space-y-1">
              <p className="text-xs font-semibold text-[#1a2529]/70">Cleanup Complete</p>
              <div className="flex items-center gap-3 text-xs text-[#1a2529]/50">
                <span>Attendance: <strong className="text-[#1a2529]">{cleanupResult.attendance_deleted}</strong></span>
                <span>Sanctions: <strong className="text-[#1a2529]">{cleanupResult.sanctions_deleted}</strong></span>
              </div>
              <p className="text-[10px] text-[#1a2529]/30">Cutoff: {cleanupResult.cutoff_date}</p>
            </div>
          )}
        </div>

        {/* Encryption Info */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
            <Shield size={16} className="text-[#026536]" /> Data Encryption
          </h3>
          <p className="text-xs text-[#1a2529]/40 mb-3">Sensitive fields encrypted at rest using AES-256 (Fernet).</p>
          <div className="space-y-2">
            {['PF Number', 'Emergency Contact Number', 'Current Address'].map(field => (
              <div key={field} className="flex items-center gap-2">
                <Badge className="bg-[#026536]/10 text-[#026536] border-0 text-xs">Encrypted</Badge>
                <span className="text-xs text-[#1a2529]/60">{field}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#1a2529]/30 mt-3">DPDP Act 2023 compliant. Employee consent collected on first login.</p>
        </div>

        {/* Data Backup */}
        <BackupCard />

        {/* Audit Logs */}
        <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-[#1a2529] text-sm flex items-center gap-2">
              <ScrollText size={16} className="text-[#026536]" /> Audit Logs
            </h3>
            <Button data-testid="toggle-audit-logs" onClick={handleShowLogs} size="sm" variant="outline" className="h-8 text-xs font-semibold">
              {showLogs ? 'Hide' : 'View Logs'}
            </Button>
          </div>
          <p className="text-xs text-[#1a2529]/40 mb-3">All data access and modifications are logged for compliance.</p>

          {showLogs && (
            <div className="animate-slide-up">
              {logsLoading ? (
                <div className="flex justify-center py-6">
                  <div className="h-6 w-6 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : logs.length === 0 ? (
                <p className="text-xs text-[#1a2529]/40 text-center py-4">No audit logs found</p>
              ) : (
                <>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {logs.map((log, i) => (
                      <div key={log.id || i} data-testid={`audit-log-${i}`} className="flex items-start gap-2 p-2.5 rounded-lg bg-[#f4f4f5] text-xs">
                        <Badge className={`${actionColors[log.action] || 'bg-gray-100 text-gray-600'} border-0 text-[10px] shrink-0 mt-0.5`}>
                          {log.action}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1a2529]/70 truncate">{log.details || '-'}</p>
                          <p className="text-[10px] text-[#1a2529]/30 mt-0.5">
                            {formatDateTime(log.timestamp)} &middot; User: {log.user_id === 'unknown' ? 'Unknown' : log.user_id.substring(0, 8) + '...'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
                      <span className="text-[10px] text-[#1a2529]/40">{logsTotal} total entries</span>
                      <div className="flex items-center gap-1">
                        <button data-testid="audit-prev" disabled={logsPage <= 1} onClick={() => fetchLogs(logsPage - 1)}
                          className="h-7 w-7 rounded flex items-center justify-center hover:bg-[#f4f4f5] disabled:opacity-30">
                          <ChevronLeft size={14} />
                        </button>
                        <span className="text-xs text-[#1a2529]/50 px-2">{logsPage} / {totalPages}</span>
                        <button data-testid="audit-next" disabled={logsPage >= totalPages} onClick={() => fetchLogs(logsPage + 1)}
                          className="h-7 w-7 rounded flex items-center justify-center hover:bg-[#f4f4f5] disabled:opacity-30">
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}


function BackupCard() {
  const [exporting, setExporting] = useState(false);

  const handleBackup = async () => {
    setExporting(true);
    try {
      const res = await adminAPI.exportBackup();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upastith_full_backup_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded successfully');
    } catch (err) {
      toast.error('Backup export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-5 border border-border/40 shadow-sm">
      <h3 className="font-bold text-[#1a2529] text-sm mb-1 flex items-center gap-2">
        <Download size={16} className="text-[#026536]" /> Data Backup
      </h3>
      <p className="text-xs text-[#1a2529]/40 mb-3">
        Download a complete backup of all employee personal data and 90-day attendance analytics.
      </p>
      <div className="bg-[#f4f4f5] rounded-xl p-3 mb-4 space-y-1.5">
        <p className="text-[11px] text-[#1a2529]/60 font-medium">Backup includes:</p>
        <div className="grid grid-cols-2 gap-1">
          {['Employee Directory', '90-Day Analytics', 'Attendance Records', 'Leave Records'].map(item => (
            <div key={item} className="flex items-center gap-1.5 text-[10px] text-[#1a2529]/50">
              <div className="h-1.5 w-1.5 rounded-full bg-[#026536]" />
              {item}
            </div>
          ))}
        </div>
      </div>
      <Button
        data-testid="backup-export-btn"
        onClick={handleBackup}
        disabled={exporting}
        className="h-10 bg-[#026536] hover:bg-[#026536]/90 font-semibold gap-2"
      >
        {exporting ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
        {exporting ? 'Generating Backup...' : 'Download Full Backup'}
      </Button>
    </div>
  );
}
